import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  INPUT_TYPE_LABEL,
  categoryOf,
  changesItems,
  itemChanges,
  routeAfterEdit,
  routeForCategory,
  type JobOrder,
  type Kit,
} from '@/domain';
import { useCatalog } from '@/application/usecases/useIntake';
import { useJobOrder, useUpdateJobOrder } from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { PageLoading } from '@/ui/feedback/Loading';
import { GoBackIcon } from '@/ui/icons';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { useOfferUndo } from '@/features/shell/useOfferUndo';
import { CaseFields } from '../intake/CaseFields';
import {
  NO_FIELD_ERRORS,
  caseFromOrder,
  caseItems,
  caseUnits,
  changesBetween,
  fieldErrorsFrom,
  mergeSpanErrors,
  patientProblem,
  spanErrorsFrom,
  spanRowErrors,
  type CaseFieldErrors,
  type CaseFormValue,
} from '../intake/caseForm';
import { EditCasePhotos } from './EditCasePhotos';
import intakeStyles from '../intake/IntakeScreen.module.css';

/**
 * Editing a case after intake. System test handover 2.4.
 *
 * The same fields intake draws, `CaseFields`, filled from the order. Behind
 * `editCase`, which the access matrix seeds to Management. The dates are not
 * here: they have their own control on the order and their own permission.
 *
 * The input type and the item change at any stage since the lab's request of
 * 18 October 2026, and so does the item on a tooth. Such a save asks a
 * question of its own, which says where the case goes from here once its
 * route changes, and the toast after it offers Undo for a moment, which puts
 * the whole save back with the prices the case had.
 *
 * Save asks first, once the form's own checks have passed: the request of
 * 5 October 2026 (third), section 2. Cancel leaves every field as it was typed,
 * and what the server refuses lands back on the form.
 *
 * The doctor and the patient open as the records the case is tied to, and
 * change by choosing another or typing a new name, as at intake: the request
 * of 6 October 2026 (second), items 4 and 5. A refusal of either is said
 * against its field as well as under the buttons. The two change apart since
 * the request of 6 October 2026 (third), item 3: another doctor leaves the
 * patient as it was.
 */
export function EditCaseScreen() {
  const { jo } = useParams<{ jo: string }>();
  const { data: session } = useSession();
  const { data, isPending, isError } = useJobOrder(jo);

  if (session?.permissions.includes('editCase') !== true) {
    return <p>{strings.editCase.notAllowed}</p>;
  }
  if (isPending) return <PageLoading label={strings.editCase.loading} />;

  const kit = data?.kits[0];
  if (isError || data === undefined || kit === undefined) return <p>{strings.errors.server}</p>;

  return (
    <EditCaseForm
      order={data.jobOrder}
      kits={data.kits}
      lead={kit}
      seesIdentity={session.permissions.includes('seeIdentity')}
    />
  );
}

function EditCaseForm({
  order,
  kits,
  lead,
  seesIdentity,
}: {
  order: JobOrder;
  kits: Kit[];
  lead: Kit;
  seesIdentity: boolean;
}) {
  const navigate = useNavigate();
  const { data: catalog } = useCatalog();
  const update = useUpdateJobOrder();
  const ask = useConfirmAction();
  const offerUndo = useOfferUndo();

  /*
   * The case as it was opened, and the version it was opened at. The order
   * refetches while this is open; the form keeps what the person is editing,
   * and the If-Match goes with the version they started from, so somebody
   * else's change in the meantime is a 409 rather than a silent overwrite.
   */
  const [before] = useState(() => caseFromOrder(order, lead));
  const [rowVersion] = useState(order.rowVersion);
  const [value, setValue] = useState(before);
  const [error, setError] = useState<string | null>(null);
  const [serverSpanErrors, setServerSpanErrors] = useState<Record<number, string>>({});
  // The server's words against the doctor and the patient, until either is touched.
  const [fieldErrors, setFieldErrors] = useState<CaseFieldErrors>(NO_FIELD_ERRORS);

  useEffect(() => {
    setServerSpanErrors({});
  }, [value.spanRows, value.teeth]);

  useEffect(() => {
    setFieldErrors(NO_FIELD_ERRORS);
  }, [value.dentistName, value.doctorId, value.patientName, value.patientId, value.newPatient]);

  /** The road a case takes with this input type and item, by the item's category on the price list. */
  const routeOf = (form: CaseFormValue) =>
    routeForCategory(
      form.input,
      catalog?.items.find((item) => item.name === form.product)?.category ?? categoryOf(form.product),
    );
  const clientSpanErrors = spanRowErrors(value.spanRows, value.teeth);
  const spanErrors = mergeSpanErrors(clientSpanErrors, serverSpanErrors);
  const recordPath = `/orders/${order.jobOrderNo}/record`;

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (seesIdentity && value.dentistName.trim() === '') {
      setError(strings.intake.dentistRequired);
      return;
    }
    if (seesIdentity && value.patientName.trim() === '') {
      setError(strings.intake.patientRequired);
      return;
    }
    // A name more than one patient has, with none of them chosen: as at intake.
    const undecided = patientProblem(value);
    if (undecided !== null) {
      setError(undecided);
      setFieldErrors({ ...NO_FIELD_ERRORS, patient: undecided });
      return;
    }
    if (caseUnits(value) === 0) {
      setError(strings.intake.noUnits);
      return;
    }
    const firstSpanError = clientSpanErrors.find((message) => message !== null) ?? null;
    if (firstSpanError !== null) {
      setError(firstSpanError);
      return;
    }

    const changes = changesBetween(before, value);
    if (Object.keys(changes).length === 0) {
      setError(strings.editCase.nothing);
      return;
    }
    setError(null);

    // The input type or an item: a question of its own, and Undo after it.
    const items = itemChanges(caseItems(before), caseItems(value));
    const question = changesItems(items)
      ? strings.confirm.saveItems(
          order.jobOrderNo,
          {
            item: items.product ? value.product : null,
            input: items.input ? INPUT_TYPE_LABEL[value.input] : null,
            teeth: items.teeth,
          },
          routeAfterEdit(
            kits.map((kit) => kit.stage),
            routeOf(before),
            routeOf(value),
          ),
        )
      : strings.confirm.saveCase(order.jobOrderNo);

    ask(
      question,
      () =>
        update.mutateAsync({ jobOrderNo: order.jobOrderNo, rowVersion, changes }).then(
          (result) => {
            // Carries Undo when the server answered with one: an edit of the input type or an item.
            offerUndo(`${order.jobOrderNo} ${strings.editCase.saved}`, result.undo);
            void navigate(recordPath);
          },
          // Handled here, on the form, so the question closes without a second word about it.
          (failure: Error) => {
            setError(failureWords(failure));
            setServerSpanErrors(spanErrorsFrom(failure));
            setFieldErrors(fieldErrorsFrom(failure));
          },
        ),
      { confirmLabel: strings.editCase.save },
    );
  }

  return (
    <div>
      <Button icon={<GoBackIcon />} onClick={() => void navigate(recordPath)}>
        {strings.editCase.back}
      </Button>

      <div className={intakeStyles.head}>
        <span className={intakeStyles.lbl}>{strings.editCase.label}</span>
        <h2>{order.jobOrderNo}</h2>
        <span className={intakeStyles.hint}>{strings.editCase.hint}</span>
      </div>

      <form className={intakeStyles.card} onSubmit={submit} noValidate>
        <CaseFields
          value={value}
          onChange={setValue}
          catalogItems={catalog?.items ?? []}
          dates={null}
          identity={seesIdentity}
          identityErrors={fieldErrors}
          spanErrors={spanErrors}
        />

        {/*
          The photographs of the case. Reception photographs what arrived in the
          box, and until now a photograph taken sideways or with a thumb over
          the impression stayed on the case for ever: there was an upload and no
          way back.
        */}
        <EditCasePhotos kitId={lead.id} jobOrderNo={lead.jobOrderNo} />

        <div className={intakeStyles.actions}>
          <Button type="button" onClick={() => void navigate(recordPath)}>
            {strings.editCase.cancel}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {update.isPending ? strings.editCase.saving : strings.editCase.save}
          </Button>
        </div>

        {error !== null && (
          <p className={intakeStyles.error} role="alert">
            {error}
          </p>
        )}
      </form>
    </div>
  );
}
